<template>
  <div class="trip-regular-table relative font-inter @container">
    <table class="table table-sm table-vertical-middle table-hover calendar-departure-table w-full @[700px]:mt-6 @[1000px]:mt-0">
      <thead v-if="showHeader">
        <tr>
          <th
            v-if="showInfo"
            class="hidden @[1000px]:table-cell ml-3 @[1000px]:ml-0"
            data-i18n="formTripdateStart"
            scope="col"
            width="10"
          />
          <th
            class="hidden @[1000px]:table-cell print-table-cell ml-3 @[1000px]:ml-0"
            data-i18n="formTripdateStart"
            scope="col"
            width="210"
          >
            {{ $t('formTripdateStart') }}
          </th>
          <th
            class="print-table-cell hidden @[1000px]:table-cell"
            data-i18n="departureTable.destination-header"
            scope="col"
          >
            {{ $t('departureTable.destination-header') }}
          </th>
          <th
            v-if="showAirport"
            class="print-table-cell hidden @[1000px]:table-cell"
            data-i18n="airport"
            scope="col"
            width="160"
          >
            {{ $t('airport') }}
          </th>
          <th
            v-if="tourGuideExists"
            class="print-hidden hidden @[1000px]:table-cell"
            data-i18n="tourguide"
            scope="col"
          >
            {{ $t('tourguide') }}
          </th>
          <th
            class="print-table-cell hidden @[1000px]:table-cell text--no-break text-left text-print-nowrap"
            data-i18n="departureTable.price-header"
            scope="col"
            width="80"
          >
            {{ $t('departureTable.price-header') }}
          </th>
          <th
            v-if="showSeatsLeft"
            class="hidden @[1000px]:table-cell print-hidden text-center"
            data-i18n="seatsLeft"
            scope="col"
          >
            {{ $t('seatsLeft') }}
          </th>
          <template v-if="hydrated && showSinglefinder">
            <th
              class="hidden @[1000px]:table-cell print-hidden text-center"
              data-i18n="memberTravelCompanions"
              scope="col"
              width="100"
            >
              {{ $t('solo.findRoommate') }}
            </th>
          </template>
          <th
            class="hidden @[1000px]:table-cell print-hidden"
            scope="col"
            width="100"
          />
        </tr>
      </thead>
      <tbody>
        <template v-if="loading || !hydrated">
          <tr
            v-for="rowIdx in 5"
            :key="`xl-r${rowIdx}`"
            class="flex @[1000px]:table-row"
          >
            <td
              v-for="colIdx in 5"
              :key="`xl-r${rowIdx}-c${colIdx}`"
              class="grow"
            >
              <b-skeleton height="1.2rem" />
            </td>
            <td
              class="grow"
            >
              <b-skeleton height="1.2rem" />
            </td>
            <td
              v-if="tourGuideExists"
              class="grow"
            >
              <b-skeleton height="1.2rem" />
            </td>
            <td
              v-if="showSeatsLeft"
              class="grow"
            >
              <b-skeleton height="1.2rem" />
            </td>
            <template v-if="hydrated && showSinglefinder">
              <td class="grow">
                <b-skeleton height="1.2rem" />
              </td>
            </template>
          </tr>
        </template>
        <template v-else>
          <client-only>
            <template
              v-for="(trip, i) in trips"
              :key="i"
            >
              <tr
                ref="tableRow"
                class="trip-row relative print:table-row flex flex-wrap @[1000px]:table-row @[700px]:rounded-lg @[700px]:overflow-hidden @[1000px]:rounded-none @[1000px]:overflow-visible"
                :data-type="trip?.airport_text ? 'flight' : 'bus'"
                :class="rowClasses(trip)"
                @click="handleRowClick(trip)"
              >
                <td
                  v-if="showInfo"
                  class="order-1 grow print-hidden calendar-info-button hidden @[1000px]:table-cell"
                >
                  <slot
                    name="info"
                    :trip="trip"
                  >
                    <button
                      class="bg-transparent p-0 text-blue h-full"
                      :aria-label="$t('otherInfo')"
                      @click="$emit('openInfo', trip)"
                    >
                      <fa icon="info-circle" />
                    </button>
                  </slot>
                </td>
                <td class="order-2 w-full @[1000px]:w-auto font-normal !pb-0 @[1000px]:!py-3 print:px-0">
                  <slot
                    name="tripDateAndAirport"
                    :trip="trip"
                  >
                    <span class="capitalize text-nowrap @[1000px]:font-normal">
                      <fa
                        v-if="showTravelIcon"
                        class="mr-1"
                        :aria-label="$t('transportIcon')"
                        size="md"
                        :icon="trip.airport_text ? 'plane' : 'bus'"
                      />
                      <slot
                        v-if="showInfo"
                        name="info"
                        :trip="trip"
                      >
                        <button
                          class="bg-transparent p-0 text-blue h-full mr-1 @[1000px]:hidden"
                          :aria-label="$t('otherInfo')"
                          @click="$emit('openInfo', trip)"
                        >
                          <fa icon="info-circle" />
                        </button>
                      </slot>
                      {{ localeDate(trip.date, localeDateCustomFormats.dateWithShortWeekday) }}
                    </span>
                  </slot>
                </td>
                <td class="order-1 order-md-4 w-7/12 @[700px]:grow @[700px]:!pt-6 @[1000px]:w-auto !pt-4 !pb-2 text-print-nowrap @[1000px]:font-secondary font-bold inline-flex @[1000px]:!py-3 @[1000px]:font-normal @[1000px]:table-cell !overflow-visible font-primary @[1000px]:font-secondary print:font-normal print:font-secondary">
                  <slot
                    name="name"
                    :trip="trip"
                  >
                    <span
                      v-if="trip?.combined"
                      :data-translation="trip.departure_name_translationkey"
                    >
                      {{ trip.departure_name }}
                    </span>
                    <span
                      v-else
                      :data-translation="trip.departure_name_translationkey"
                    >
                      {{ trip.tripname }}
                    </span>
                    <TripSingleFinderList
                      v-if="showSinglefinder"
                      class="hidden @[700px]:block @[1000px]:hidden ml-6 -mt-0.5"
                      :tripid="trip.tripid"
                      :registered-persons-list="trip.single_finder_list"
                      :registered-persons-count="trip.single_finder_count"
                    />
                  </slot>
                </td>
                <td
                  v-if="showAirport"
                  class="order-5 w-12/12 @[1000px]:w-auto !pt-2 !pb-1 @[1000px]:!py-3 text-break text-print-nowrap text-sm @[700px]:text-base print:text-base"
                >
                  <slot
                    name="airport"
                    :trip="trip"
                  >
                    <span
                      v-if="trip.airport_text"
                      class="text-gray-800 font-semibold @[1000px]:hidden print-hidden"
                    >
                      Flygplats:
                    </span>{{ trip.airport_text }}
                  </slot>
                </td>
                <td
                  v-if="tourGuideExists"
                  class="order-5 w-12/12 @[1000px]:w-auto !py-1 print-hidden text-sm @[1000px]:!py-3 @[700px]:text-base"
                >
                  <slot
                    name="tourleader"
                    :trip="trip"
                  >
                    <template v-if="trip.tourleader_name">
                      <span
                        class="@[1000px]:hidden print-hidden font-semibold text-gray-800"
                        data-i18n="reviewLabelTourleader"
                      >
                        {{ $t('reviewLabelTourleader') }}&nbsp;
                      </span>
                      <nuxt-link
                        v-if="trip.tourleaderUrl"
                        class="text-dark-black-blue"
                        :to="trip.tourleaderUrl"
                      >
                        {{ trip.tourleader_name }}
                      </nuxt-link>
                      <nuxt-link
                        v-else-if="trip.tourleader_id"
                        class="text-dark-black-blue"
                        :to="`${localeURLs.tourleader}/${trip.tourleader_id}`"
                      >
                        {{ trip.tourleader_name }}
                      </nuxt-link>
                      <span v-else>
                        {{ trip.tourleader_name }}
                      </span>
                    </template>
                  </slot>
                </td>
                <td class="order-1 ml-auto grow table-cell !pt-4 !pb-2 @[700px]:hidden @[1000px]:!py-3 @[1000px]:table-cell text-right @[1000px]:text-left font-secondary !overflow-visible z-[1] print:table-cell">
                  <slot
                    name="price"
                    :trip="trip"
                  >
                    <div class="h-full flex items-end @[1000px]:items-start justify-start @[1000px]:justify-end flex-col absolute @[1000px]:relative print:relative right-0 mr-4">
                      <DeparturesTablePrice :trip="trip" />
                    </div>
                  </slot>
                </td>
                <td
                  v-if="showSeatsLeft"
                  class="order-8 w-full order-md-3 ml-md-auto @[1000px]:w-auto @[1000px]:ml-0 print-hidden self-center text-left @[1000px]:text-center text-sm @[700px]:text-base @[1000px]:text-dark-black-blue !py-1 @[1000px]:!py-3"
                >
                  <slot
                    name="seats"
                    :trip="trip"
                  >
                    <span
                      class="@[1000px]:hidden font-bold text-gray-800"
                      data-i18n="seatsLeft"
                    >
                      {{ $t('seatsLeft') }}:&nbsp;
                    </span>
                    <span class="capitalize font-normal">
                      {{ trip.seats_left > 0 ? seatsLeft(trip.seats_left) : $t('tripFull') }}
                    </span>
                  </slot>
                </td>
                <client-only>
                  <template v-if="showSinglefinder">
                    <td
                      class="order-8 @[700px]:order-1 @[1000px]:order-8 my-auto print-hidden grow @[700px]:flex @[1000px]:table-cell items-end justify-end !overflow-visible"
                      :class="trip.discounted ? '@[700px]!pb-12' : '@[700px]!pb-8'"
                    >
                      <strong class="block mb-1 @[700px]:hidden text-sm">
                        {{ $t('solo.findRoommate') }}
                      </strong>

                      <TripSingleFinderList
                        class="@[700px]:hidden @[1000px]:block"
                        :tripid="trip.tripid"
                        :registered-persons-list="trip.single_finder_list"
                        :registered-persons-count="trip.single_finder_count"
                      />

                      <div class="flex flex-col items-start">
                        <slot
                          name="singlefinders"
                          :trip="trip"
                        >
                          <strong class="hidden mb-1 @[700px]:block @[1000px]:hidden">
                            {{ $t('solo.findRoommate') }}
                          </strong>

                          <TripSingleFinderRegister
                            label-side="right"
                            :tripid="trip.tripid"
                            :registered-persons-list="trip.single_finder_list"
                            :registered-persons-count="trip.single_finder_count"
                          />
                        </slot>
                      </div>
                    </td>
                  </template>
                </client-only>
                <td class="order-9 ml-auto @[1000px]:ml-0 mt-auto @[700px]:!py-0 @[1000px]:!py-3 @[1000px]:mt-0 print-hidden w-full @[1000px]:w-auto !overflow-visible">
                  <div class="@[700px]:!absolute bottom-0 right-0 @[1000px]:!relative @[700px]:mx-4 @[1000px]:mx-0">
                    <slot
                      name="price"
                      :trip="trip"
                    >
                      <div class="hidden @[700px]:flex @[1000px]:hidden h-full items-end @[1000px]:items-start justify-center @[1000px]:justify-end flex-col">
                        <DeparturesTablePrice :trip="trip" />
                      </div>
                    </slot>
                    <slot
                      name="action"
                      :trip="trip"
                    >
                      <template v-if="isBookDepartures">
                        <nuxt-link
                          v-if="trip.bookable"
                          class="btn btn-secondary rounded-3xl font-bold normal-case text-lg py-1 @[1000px]:py-0.5 flex items-center justify-center font-primary ml-auto @[1000px]:my-0 @[1000px]:w-fit w-full mt-2"
                          :to="rowLink(trip)"
                        >
                          <span>
                            {{ $t('bookButton') }}
                          </span>
                          <fa
                            icon="arrow-right"
                            class="ml-2"
                          />
                        </nuxt-link>
                        <button
                          v-else
                          class="calendar-action-button px-4 rounded-3xl btn bg-brick-red text-white my-2"
                          @click="$emit('openWaitinglist', trip)"
                        >
                          {{ $t('waitingListButton') }}
                        </button>
                      </template>
                      <nuxt-link
                        v-else
                        class="calendar-action-button btn-secondary text--no-break text-transform-none font-primary font-semibold my-0 @[1000px]:my-1 py-2 @[1000px]:py-1 flex items-center @[1000px]:w-auto w-full mt-3 @[1000px]:mt-0 justify-center"
                        :to="`/${trip.triptypeurl}`"
                        data-i18n="readmoreText"
                      >
                        {{ $t('readmoreText') }}
                        <fa
                          class="pl-1"
                          icon="arrow-right"
                        />
                      </nuxt-link>
                    </slot>
                  </div>
                </td>
                <td class="order-last h-4 !p-0 bg-white w-full hidden @[700px]:block @[1000px]:hidden" />
              </tr>

              <tr class="!p-0 h-[10px]" />
            </template>
          </client-only>
        </template>
      </tbody>
    </table>
    <div
      v-show="selectedMonth !== null"
      ref="selectedMonthMarker"
      :key="selectedMonth"
      class="absolute border-[3px] border-orange w-full rounded-2xl pointer-events-none fade-in"
    />
  </div>
</template>

<script>
import calendarMixin from '@layers/web/mixins/calendar.mixin'
import '@layers/web/assets/scss/pages/_calendar.scss'

export default defineNuxtComponent({
  async setup () {
    const { urls: localeURLs } = useLocale()
    const { localeDate, localeDateCustomFormats } = useDate()

    const userStore = useUserStore()

    const { isLoggedIn } = storeToRefs(userStore)

    const { isNarrower, screenWidth } = useScreen()

    const { isSingleFinderActive } = await useSingleFinder()

    useAsyncData(async () => {
      if (!isLoggedIn.value) {
        return
      }

      await Promise.all([
        userStore.fetchMemberProfile(),
        userStore.fetchMemberSingleFinderList(),
      ])
    })

    return {
      localeURLs,
      localeDate,
      localeDateCustomFormats,
      isSingleFinderActive,
      isNarrower,
      screenWidth,
    }
  },

  mixins: [calendarMixin],

  props: {
    tourGuideExists: {
      type: Boolean,
      default: false,
    },

    showSeatsLeft: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    trips: {
      type: Array,
      default: () => [],
    },

    showInfo: {
      type: Boolean,
      default: false,
    },

    markRowStatus: {
      type: Boolean,
      default: false,
    },

    showTravelIcon: {
      type: Boolean,
      default: false,
    },

    showAirport: {
      type: Boolean,
      default: true,
    },

    isBookDepartures: {
      type: Boolean,
      default: false,
    },

    hideHeaderUnlessSingleDeparture: {
      type: Boolean,
      default: false,
    },

    hideSinglefinder: {
      type: Boolean,
      default: false,
    },
  },

  expose: ['selectMonth', 'markSelectedMonth'],

  emits: ['marking', 'openWaitinglist', 'openInfo'],

  data () {
    return {
      selectedMonth: null,
      hydrated: false,
      isRowLinks: false,
    }
  },

  mounted () {
    this.hydrated = true
  },

  computed: {
    showHeader () {
      return !this.hideHeaderUnlessSingleDeparture || this.trips.length <= 1
    },

    showSinglefinder () {
      return this.isSingleFinderActive && !this.hideSinglefinder
    },
  },

  watch: {
    screenWidth () {
      if (this.isBookDepartures) {
        return
      }

      if (!this.isRowLinks && this.isNarrower(1000)) {
        this.isRowLinks = true
      } else if (this.isRowLinks) {
        this.isRowLinks = false
      }
    },
  },

  methods: {
    selectMonth (m) {
      this.selectedMonth = m
    },

    markSelectedMonth (monthRowIndices = [], shouldScroll = false) {
      if (this.selectedMonth === null) {
        return
      }

      const monthStartIndex = monthRowIndices[0]
      const monthEndIndex = monthRowIndices[monthRowIndices.length - 1]

      const onMarkSelectedMonth = () => {
        let monthMarkerHeightPx

        const monthMarker = this.$refs.selectedMonthMarker
        const monthStartRow = this.$refs.tableRow[monthStartIndex]
        const monthEndRow = this.$refs.tableRow[monthEndIndex]

        if (monthMarker) {
          if (monthStartRow && monthEndRow) {
            monthMarker.style.display = 'block'
            const monthMarkerBorderWidth = parseInt(getComputedStyle(this.$refs.selectedMonthMarker).getPropertyValue('border-left-width'))
            monthMarker.style.top = `${monthStartRow.offsetTop - (monthMarkerBorderWidth / 2)}px`
            monthMarkerHeightPx = (monthEndRow.offsetTop + monthEndRow.offsetHeight) - monthStartRow.offsetTop + monthMarkerBorderWidth
            monthMarker.style.height = `${monthMarkerHeightPx}px`
          } else {
            monthMarker.style.display = 'none'
          }
        }

        if (shouldScroll === true) {
          if (monthMarkerHeightPx > window.innerHeight - 200 && monthStartRow) {
            const scrollTo = monthStartRow.getBoundingClientRect()?.top + window.scrollY - 100

            window.scrollTo({
              top: scrollTo,
              behavior: 'smooth',
            })
          } else {
            const monthMiddleIndex = monthRowIndices[Math.ceil((monthRowIndices.length - 1) / 2)]
            const monthMiddleRow = this.$refs.tableRow[monthMiddleIndex]

            if (monthMiddleRow) {
              monthMiddleRow.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              })
            }
          }
        }
      }

      this.$emit('marking', monthEndIndex)
      this.$nextTick(onMarkSelectedMonth)
    },

    rowClasses (trip) {
      const classes = []

      if (this.markRowStatus && !trip.bookable) {
        classes.push('trip-full')
      }
      if (this.markRowStatus && trip.group) {
        classes.push('trip-row-gray')
      }
      if (this.isRowLinks) {
        classes.push('trip-row-hover cursor-pointer')
      }

      return classes.join(' ')
    },

    rowLink (trip) {
      return `${this.localeURLs.book}/${trip.tripid}?price=${trip.price}&singlePrice=${trip.single_price}`
    },

    handleRowClick (trip) {
      if (this.isRowLinks) {
        this.$router.push({
          path: `/${trip.triptypeurl}`,
        })
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.dot {
  width: 18px;
  height: 18px;
  background-color: theme('colors.alt-gray');
  border-radius: 50%;
  display: inline-block;

  &-active {
    background-color: theme('colors.blue.DEFAULT');
  }
}
</style>
